import { TradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/shared';

import { capTradesToByteBudget } from './block-trade-context';
import type { TapeSnapshot } from './tape-snapshot-store';

/**
 * Byte budget for the tape portion of the `/analyze` context. The whole
 * `chat.send` frame — prompt + system preamble + analyzed trade + tape —
 * shares the gateway-proxy's 32 KiB client read limit (see
 * `block-trade-context.ts`); 20 KiB here leaves the rest of the frame ample
 * headroom.
 */
export const TAPE_CONTEXT_BYTES = 20 * 1024;

/**
 * The VISIBLE `/analyze` prompt — what shows in the chat bubble. Just the rfq
 * id + human-readable summary; the full data rides separately as hidden
 * context (see `buildTradeAnalysisContext`) so the bubble stays clean.
 */
export function buildTradeAnalysisPrompt(trade: TradeTapeItem): string {
  return `/analyze ${trade.rfq_id} ${trade.description}`;
}

/**
 * HIDDEN context for the `/analyze`d trade — the full structured row plus the
 * last-known tape snapshot, sent to the gateway via
 * `sendMessage(text, { context })` but never shown in the bubble. This is
 * what actually gives the agent size/legs/premium/IV/side/mark to analyze,
 * since the gateway doesn't implement `set_block_trade_context`; the tape
 * section (byte-capped) gives it surrounding flow to compare against.
 * Serialization failures fall back to no context (the prompt still sends).
 */
export function buildTradeAnalysisContext(
  trade: TradeTapeItem,
  tape?: TapeSnapshot | null,
): string {
  try {
    const parts = [
      `Trade data for ${trade.rfq_id} (JSON):\n${JSON.stringify(trade)}`,
    ];
    const kept =
      tape == null
        ? []
        : capTradesToByteBudget(tape.trades, TAPE_CONTEXT_BYTES);
    if (tape != null && kept.length > 0) {
      parts.push(
        `Full tape — latest ${kept.length} of ${tape.visibleCount} visible ` +
          `block trades, captured ${new Date(tape.capturedAt).toISOString()} ` +
          `(JSON):\n${JSON.stringify(kept)}`,
      );
    }
    return parts.join('\n\n');
  } catch {
    return '';
  }
}
